import { FROM_TABLET } from "@/globals";
import { waitForScrollRestore } from "@/utils/scroll";

// "forward" pushes a deeper screen in from the right, "back" returns to the
// previous one. Read by the ::view-transition rules in tailwind.css.
export type ViewTransitionDirection = "forward" | "back";

// Declarative <BrowserRouter> has no `viewTransition` option (data routers
// only), so the browser API is driven directly. `update` must resolve once
// the new route has committed: the browser takes its "after" snapshot as soon
// as the callback's promise settles, so resolving early animates towards the
// old screen and the new one pops in afterwards. Falls back to an instant
// update where the API is missing or the user asked for reduced motion.
const FALLBACK_MS = 300;

export function runViewTransition(
  direction: ViewTransitionDirection,
  update: () => Promise<void>
) {
  // Desktop shows the list and the screen side by side, so there is no
  // full-screen push/pop to animate.
  const isSupported =
    typeof document.startViewTransition === "function" &&
    window.innerWidth < FROM_TABLET &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!isSupported) {
    void update();
    return;
  }
  document.documentElement.dataset["viewTransition"] = direction;

  // The browser only calls the transition's callback once it has rendered a
  // frame. A page that isn't rendering (background or occluded window,
  // headless run) would never get there and the navigation would hang, so
  // after a short wait the update is run without the animation.
  let startedUpdate: Promise<void> | undefined;
  const startUpdate = () => (startedUpdate ??= update());
  const fallbackTimer = setTimeout(() => void startUpdate(), FALLBACK_MS);

  const transition = document.startViewTransition(async () => {
    clearTimeout(fallbackTimer);
    await startUpdate();
    // Let the new screen's effects run (list scroll restore starts in one),
    // then wait for that restore's first write.
    await new Promise<void>((resolve) => setTimeout(resolve));
    await waitForScrollRestore();
  });
  // A skipped transition rejects these; the navigation itself still happened.
  transition.ready.catch(() => undefined);
  transition.finished.catch(() => undefined);
}
